import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, FileVideo } from "lucide-react";
import axios from "axios";
import { supabase } from "../lib/supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Warmup Render
async function warmupBackend() {
  if (!BACKEND_URL) return;
  const url = `${BACKEND_URL}/health`;
  const maxTries = 8;
  for (let i = 0; i < maxTries; i++) {
    try {
      const res = await fetch(url, { method: "GET", cache: "no-store" });
      if (res.ok) return;
    } catch {}
    await new Promise((r) => setTimeout(r, 900));
  }
}

function formatAxiosError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail ?? err?.response?.data ?? err?.message;

  if (status) {
    return `Error ${status}: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`;
  }
  return `Error: ${err?.message || "Network Error"}`;
}

export default function UploadVideo() {
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [videoType, setVideoType] = useState("music");

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const [sessionReady, setSessionReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);

  const api = useMemo(
    () =>
      axios.create({
        baseURL: API,
        timeout: 120000,
      }),
    []
  );

  // ✅ Asegura sesión antes de dejarte subir
  useEffect(() => {
    (async () => {
      try {
        await warmupBackend();

        const { data } = await supabase.auth.getSession();
        const session = data?.session;

        setSessionReady(true);
        setHasSession(!!session);

        // Si no hay sesión, fuera
        if (!session) {
          setError("Tu sesión no está activa. Vuelve a iniciar sesión.");
          navigate("/login");
        }
      } catch (e) {
        console.error(e);
        setSessionReady(true);
        setHasSession(false);
        setError("No se pudo comprobar la sesión. Vuelve a iniciar sesión.");
        navigate("/login");
      }
    })();
  }, [navigate]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setError("");

    if (f && !title.trim()) {
      const name = f.name.replace(/\.[^/.]+$/, "");
      setTitle(name);
    }
  };

  const getToken = async () => {
    const { data } = await supabase.auth.getSession();
    const session = data?.session;

    const token = session?.access_token; // igual que RecordVideo
    if (!token) {
      throw new Error("AuthSessionMissingError: Auth session missing!");
    }
    return token;
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError("");

    if (!BACKEND_URL) {
      setError("Falta REACT_APP_BACKEND_URL en Vercel.");
      return;
    }
    if (!sessionReady) {
      setError("Cargando sesión... espera 1 segundo y reintenta.");
      return;
    }
    if (!hasSession) {
      setError("No hay sesión activa. Vuelve a iniciar sesión.");
      navigate("/login");
      return;
    }
    if (!file) {
      setError("Selecciona un vídeo.");
      return;
    }
    if (!title.trim()) {
      setError("Completa el título.");
      return;
    }

    setUploading(true);

    try {
      await warmupBackend();

      const token = await getToken();

      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);
      formData.append("description", description);
      formData.append("video_type", videoType);

      // ❗ NO pongas Content-Type manual
      await api.post("/videos/upload", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      navigate("/dashboard");
    } catch (err) {
      console.error("Upload error:", err);

      // Si el error es de sesión, te mando a login directamente
      const msg = String(err?.message || "");
      if (msg.includes("AuthSessionMissingError") || msg.includes("session missing")) {
        setError("Tu sesión ha caducado. Vuelve a iniciar sesión.");
        navigate("/login");
        return;
      }

      setError(formatAxiosError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      <div className="bg-white/10 border-b border-white/20">
        <div className="container mx-auto px-4 py-4 flex justify-between">
          <Link to="/dashboard" className="text-white flex items-center gap-2">
            <ArrowLeft size={18} /> Volver
          </Link>
          <span className="text-white text-2xl font-bold">
            Arti<span className="text-yellow-400">Book</span>
          </span>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10 max-w-3xl">
        <h1 className="text-4xl text-white text-center mb-8 font-bold">Subir Video</h1>

        {error && <div className="bg-red-500 text-white px-4 py-3 rounded mb-6">{error}</div>}

        <form onSubmit={handleUpload} className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
          <label className="block text-white font-semibold mb-2">
            Seleccionar Video <span className="text-red-400">*</span>
          </label>

          <div className="border-2 border-dashed border-white/40 rounded-lg p-6 mb-6 text-center">
            <input type="file" accept="video/*" onChange={handleFileChange} className="hidden" id="videoFile" />
            <label htmlFor="videoFile" className="cursor-pointer flex flex-col items-center gap-2">
              {!file ? (
                <>
                  <Upload className="w-10 h-10 text-white/70" />
                  <span className="text-white/80">Haz click para elegir un vídeo</span>
                </>
              ) : (
                <>
                  <FileVideo className="w-10 h-10 text-green-300" />
                  <div className="text-white font-semibold">{file.name}</div>
                  <div className="text-white/70 text-sm">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                </>
              )}
            </label>
          </div>

          <label className="block text-white font-semibold mb-2">
            Título <span className="text-red-400">*</span>
          </label>
          <input className="w-full mb-6 p-3 rounded-lg" value={title} onChange={(e) => setTitle(e.target.value)} />

          <label className="block text-white font-semibold mb-2">Descripción (Opcional)</label>
          <textarea
            className="w-full mb-6 p-3 rounded-lg"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <label className="block text-white font-semibold mb-2">
            Tipo de Video <span className="text-red-400">*</span>
          </label>
          <select className="w-full mb-8 p-3 rounded-lg" value={videoType} onChange={(e) => setVideoType(e.target.value)}>
            <option value="dance">Danza</option>
            <option value="music">Música</option>
          </select>

          <button
            type="submit"
            disabled={uploading || !sessionReady || !hasSession}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition disabled:bg-gray-500"
          >
            {uploading ? "Subiendo..." : "Subir Video"}
          </button>
        </form>
      </div>
    </div>
  );
}
